/**
 * AK-423 — TanStack Query хуки + мутации над field_card API.
 * Все пути — /api/v1/fields/{id}/... (backend field_card.py).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import { goalDisplayName, type GrowingGoalOption } from './growingGoals';
import type {
  ContourVersion,
  CropHistoryRow,
  JournalEvent,
  FieldSnapshot,
  OccupancyCreateBody,
  OccupancyPatchBody,
  OccupancySowing,
  OccupancyView,
  Subzone,
  SubzoneCreateBody,
} from './types';

export const fieldCardKeys = {
  occupancy: (fieldId: string, seasonId: string | null) =>
    ['field-card', 'occupancy', fieldId, seasonId] as const,
  cropHistory: (fieldId: string) =>
    ['field-card', 'crop-history', fieldId] as const,
  journal: (fieldId: string, type: string | null) =>
    ['field-card', 'journal', fieldId, type] as const,
  subzones: (fieldId: string) => ['field-card', 'subzones', fieldId] as const,
  contourHistory: (fieldId: string) =>
    ['field-card', 'contour-history', fieldId] as const,
};

/** Ключи справочника назначений посева (общие с reference-модулем по смыслу). */
export const growingGoalKeys = {
  all: () => ['reference', 'growing-goals'] as const,
  cropGoals: (cropId: string) => ['reference', 'crop-goals', cropId] as const,
};

interface GoalsEnvelope {
  items?: Array<{
    id: string;
    code?: string | null;
    name_translations?: Record<string, string> | null;
  }> | null;
}

/**
 * GET /reference/growing-goals — справочник «Назначение посева» (7 целей:
 * товарное зерно, семена, силос, сенаж, зелёный корм, сидерат, переработка).
 *
 * Ответ — конверт `{ items }`; массив в корне поддерживаем defensive (как в
 * useCropsReference), чтобы форма не падала на нестандартном ответе.
 */
export function useGrowingGoals(): UseQueryResult<GrowingGoalOption[], Error> {
  return useQuery<GrowingGoalOption[], Error>({
    queryKey: growingGoalKeys.all(),
    queryFn: async () => {
      const resp = await api.get<GoalsEnvelope>(
        '/api/v1/reference/growing-goals',
      );
      const items = Array.isArray(resp) ? resp : (resp?.items ?? []);
      return items.map((g) => ({
        id: g.id,
        code: g.code ?? '',
        name: goalDisplayName(g),
      }));
    },
    staleTime: 5 * 60_000,
  });
}

/**
 * GET /reference/crops/{id} → `growing_goal_ids` — какие назначения допустимы
 * для выбранной культуры. Деталь культуры доступна любому авторизованному
 * пользователю (не только platform-admin).
 *
 * `retry: false` + мягкая обработка: упал запрос → SowForm показывает весь
 * справочник, а не блокирует выбор.
 */
export function useCropGoalIds(
  cropId: string | null,
): UseQueryResult<string[], Error> {
  return useQuery<string[], Error>({
    queryKey: growingGoalKeys.cropGoals(cropId ?? ''),
    enabled: Boolean(cropId),
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const resp = await api.get<{ growing_goal_ids?: string[] | null }>(
        `/api/v1/reference/crops/${cropId}`,
      );
      return Array.isArray(resp?.growing_goal_ids) ? resp.growing_goal_ids : [];
    },
  });
}

/**
 * Номенклатура культур ХОЗЯЙСТВА для формы посева (SA-04).
 *
 * Раньше форма посева брала весь системный справочник `reference.crops` —
 * агроном видел сотни культур, которых у хозяйства не было, а пометка
 * «не культура» на выбор не влияла. Теперь источник — номенклатура хозяйства;
 * системный справочник остаётся только за кнопкой «добавить из общего».
 */
export interface CropPickerItem {
  local_catalog_item_id: string | null;
  crop_id: string | null;
  name: string;
  canonical_name: string | null;
  /** matched | suggested | queued; null — культуры нет в номенклатуре */
  match_status: string | null;
  in_nomenclature: boolean;
  color: string | null;
}

/** Ключ строки в выпадающем списке (у культуры вне номенклатуры нет lci-id). */
export function cropPickerKey(item: CropPickerItem): string {
  return item.local_catalog_item_id ?? `crop:${item.crop_id ?? ''}`;
}

export const cropPickerKeys = {
  all: () => ['field-card', 'crop-picker'] as const,
};

/**
 * GET /reference/tenant-crops/picker — что можно посеять у этого хозяйства.
 *
 * `enabled` — для форм, где номенклатура нужна лишь одному разделу (форма
 * позиции бюджета тянет культуры только у семян): без гейта диалог СЗР или
 * удобрений слал бы запрос за списком, который никогда не покажет.
 */
export function useCropPicker(enabled = true): UseQueryResult<CropPickerItem[], Error> {
  return useQuery<CropPickerItem[], Error>({
    enabled,
    queryKey: cropPickerKeys.all(),
    queryFn: async () => {
      // defensive: нестандартный ответ не должен ронять форму посева
      const resp = await api.get<CropPickerItem[]>(
        '/api/v1/reference/tenant-crops/picker',
      );
      return Array.isArray(resp) ? resp : [];
    },
    staleTime: 60_000,
  });
}

/**
 * POST /reference/tenant-crops/from-canon — завести культуру из общего
 * справочника в номенклатуру хозяйства. Идемпотентно: культура уже заведена →
 * вернётся существующая строка.
 */
export function useAdoptCanonCrop() {
  const qc = useQueryClient();
  return useMutation<CropPickerItem, Error, { crop_id: string }>({
    mutationFn: (body) =>
      api.post<CropPickerItem>('/api/v1/reference/tenant-crops/from-canon', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: cropPickerKeys.all() });
    },
  });
}

/** GET /fields/{id}/subzones?include_geojson=true — активные зоны поля + геометрия. */
export function useSubzones(
  fieldId: string,
): UseQueryResult<Subzone[], Error> {
  return useQuery<Subzone[], Error>({
    queryKey: fieldCardKeys.subzones(fieldId),
    enabled: Boolean(fieldId),
    queryFn: () =>
      api.get<Subzone[]>(
        `/api/v1/fields/${fieldId}/subzones?include_geojson=true`,
      ),
    staleTime: 30_000,
  });
}

/** POST /fields/{id}/subzones — создать зону. Инвалидирует зоны + карточку. */
export function useCreateSubzone(fieldId: string) {
  const qc = useQueryClient();
  return useMutation<Subzone, Error, SubzoneCreateBody>({
    mutationFn: (body) =>
      api.post<Subzone>(`/api/v1/fields/${fieldId}/subzones`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['field-card'] });
    },
  });
}

/**
 * PATCH /fields/{id}/subzones/{subzone_id} — правка зоны.
 *
 * ⚠️ У `valid_from` ДВА разных смысла, и они зависят от того, пришла ли
 * геометрия (докстринг backend `field_card.update_subzone`):
 *
 * * **без** `geojson` — это **исправление даты рождения зоны** («завёл
 *   дефолтной датой, потом понял, что круги с мая»). Правится сама зона,
 *   новой версии границы не появляется.
 * * **с** `geojson` (AK-515) — это «**с какой даты действует НОВАЯ граница**».
 *   Дату рождения зоны такая правка НЕ трогает (иначе июльская правка
 *   «омолодила» бы майскую зону), а в ленту версий уходит строка «Границы
 *   зоны изменены». Дата не может быть раньше предыдущей версии границы —
 *   иначе сервер ответит 422.
 *
 * `change_reason` — свободная пометка «почему поправили», видна в ленте
 * версий и в журнале поля. В обоих случаях будущее сервер отклоняет (422).
 */
export function useUpdateSubzone(fieldId: string) {
  const qc = useQueryClient();
  return useMutation<
    Subzone,
    Error,
    {
      subzoneId: string;
      body: {
        valid_from?: string | null;
        geojson?: { type: string; coordinates: unknown } | null;
        change_reason?: string | null;
      };
    }
  >({
    mutationFn: ({ subzoneId, body }) =>
      api.patch<Subzone>(
        `/api/v1/fields/${fieldId}/subzones/${subzoneId}`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['field-card'] });
    },
  });
}

/** DELETE /fields/{id}/subzones/{subzone_id} — архивировать зону. */
export function useDeleteSubzone(fieldId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (subzoneId) =>
      api.delete<void>(`/api/v1/fields/${fieldId}/subzones/${subzoneId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['field-card'] });
    },
  });
}

/**
 * GET /fields/{id}/contours/history — все сохранённые геометрии поля, свежие
 * сверху (пакет 1 «История геометрии»). Показывает и версии контура, и
 * промежуточные сохранения того же дня — откатиться можно на любую.
 */
export function useContourHistory(
  fieldId: string,
): UseQueryResult<ContourVersion[], Error> {
  return useQuery<ContourVersion[], Error>({
    queryKey: fieldCardKeys.contourHistory(fieldId),
    enabled: Boolean(fieldId),
    queryFn: async () => {
      const resp = await api.get<ContourVersion[]>(
        `/api/v1/fields/${fieldId}/contours/history`,
      );
      return Array.isArray(resp) ? resp : [];
    },
    staleTime: 30_000,
  });
}

/**
 * POST /fields/{id}/geometry/restore — вернуть контур из выбранной версии.
 *
 * Откат идёт обычным сохранением: появляется новая версия «Контур
 * восстановлен», прежние строки остаются. Поэтому откат можно откатить.
 */
export function useRestoreFieldGeometry(fieldId: string) {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { history_id: string; source?: string | null }
  >({
    mutationFn: ({ history_id, source }) =>
      api.post(`/api/v1/fields/${fieldId}/geometry/restore`, {
        history_id,
        ...(source ? { source } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['field-contour-geo', fieldId] });
      void qc.invalidateQueries({ queryKey: ['field-card'] });
    },
  });
}

/**
 * PUT /fields/{id}/geometry — заменить геометрию поля из редактора контура
 * (AK-371 backend). area_ha пересчитывается сервером. Инвалидирует контур
 * (мини-карта + подпись площади), зоны и карточку.
 *
 * AK-447: `valid_from` (YYYY-MM-DD) — дата, с которой действует новый контур.
 * Ею же бэкенд закрывает предыдущую версию контура. Не передали — сервер
 * возьмёт момент правки.
 */
export function useUpdateFieldGeometry(fieldId: string) {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    {
      geometry: { type: 'Polygon' | 'MultiPolygon'; coordinates: unknown };
      valid_from?: string | null;
    }
  >({
    mutationFn: ({ geometry, valid_from }) =>
      api.put(`/api/v1/fields/${fieldId}/geometry`, {
        geometry,
        ...(valid_from ? { valid_from } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['field-contour-geo', fieldId] });
      void qc.invalidateQueries({ queryKey: ['field-card'] });
    },
  });
}

/** GET /fields/{id}/occupancy?season_id=... — leaf-раскладка занятий. */
export function useOccupancy(
  fieldId: string,
  seasonId: string | null,
): UseQueryResult<OccupancyView, Error> {
  return useQuery<OccupancyView, Error>({
    queryKey: fieldCardKeys.occupancy(fieldId, seasonId),
    enabled: Boolean(fieldId && seasonId),
    queryFn: () =>
      api.get<OccupancyView>(
        `/api/v1/fields/${fieldId}/occupancy?season_id=${seasonId}`,
      ),
    staleTime: 30_000,
  });
}

/** GET /fields/{id}/crop-history — история культур поля по сезонам (план/факт ц/га). */
export function useCropHistory(
  fieldId: string,
): UseQueryResult<CropHistoryRow[], Error> {
  return useQuery<CropHistoryRow[], Error>({
    queryKey: fieldCardKeys.cropHistory(fieldId),
    enabled: Boolean(fieldId),
    queryFn: () =>
      api.get<CropHistoryRow[]>(`/api/v1/fields/${fieldId}/crop-history`),
    staleTime: 30_000,
  });
}

/** GET /fields/{id}/journal?type=... — единая лента событий. */
export function useJournal(
  fieldId: string,
  type: string | null,
): UseQueryResult<JournalEvent[], Error> {
  return useQuery<JournalEvent[], Error>({
    queryKey: fieldCardKeys.journal(fieldId, type),
    enabled: Boolean(fieldId),
    queryFn: () => {
      const qs = type ? `?type=${encodeURIComponent(type)}` : '';
      return api.get<JournalEvent[]>(`/api/v1/fields/${fieldId}/journal${qs}`);
    },
    staleTime: 15_000,
  });
}

/** GET /fields/{id}/journal/as-of?date=... — снимок поля на дату. */
export async function fetchFieldSnapshot(
  fieldId: string,
  date: string,
): Promise<FieldSnapshot> {
  return api.get<FieldSnapshot>(
    `/api/v1/fields/${fieldId}/journal/as-of?date=${encodeURIComponent(date)}`,
  );
}

/** POST /fields/{id}/occupancy — создать занятие на leaf. */
export function useCreateOccupancy(fieldId: string) {
  const qc = useQueryClient();
  return useMutation<OccupancySowing, Error, OccupancyCreateBody>({
    mutationFn: (body) =>
      api.post<OccupancySowing>(`/api/v1/fields/${fieldId}/occupancy`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['field-card'] });
    },
  });
}

/** PATCH /fields/{id}/occupancy/{sowing_id} — правка занятия. */
export function usePatchOccupancy(fieldId: string) {
  const qc = useQueryClient();
  return useMutation<
    OccupancySowing,
    Error,
    { sowingId: string; body: OccupancyPatchBody }
  >({
    mutationFn: ({ sowingId, body }) =>
      api.patch<OccupancySowing>(
        `/api/v1/fields/${fieldId}/occupancy/${sowingId}`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['field-card'] });
    },
  });
}

/**
 * DELETE /fields/{id}/sowings/{sowing_id} — снять посев с участка (soft delete
 * на сервере, данные не теряем). Нужен как выход из ситуации «поле засеяно
 * целиком, а сеять надо по зонам» (AK-449) — без него объяснение упиралось бы
 * в тупик.
 */
export function useDeleteOccupancy(fieldId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (sowingId) =>
      api.delete<void>(`/api/v1/fields/${fieldId}/sowings/${sowingId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['field-card'] });
    },
  });
}

/** PUT /fields/{id}/land-category — сменить тип угодья. */
export function useSetLandCategory(fieldId: string) {
  const qc = useQueryClient();
  return useMutation<
    { field_id: string; land_category_id: string | null; land_category_code: string | null },
    Error,
    { land_category_code: string }
  >({
    mutationFn: (body) =>
      api.put(`/api/v1/fields/${fieldId}/land-category`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['field-card'] });
    },
  });
}

/**
 * PATCH /fields/{id} — признак «рисовая система» (fields.is_rice_system).
 * Только у таких полей в форме посева предлагается АМП. Ставится агрономом
 * руками; массовая разметка по истории — скриптом mark_rice_system_fields.py.
 */
export function useSetRiceSystem(fieldId: string) {
  const qc = useQueryClient();
  return useMutation<{ id: string; is_rice_system: boolean }, Error, boolean>({
    mutationFn: (isRiceSystem) =>
      api.patch(`/api/v1/fields/${fieldId}`, { is_rice_system: isRiceSystem }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['field-card'] });
    },
  });
}

/** POST /fields/{id}/lifecycle — переход жизненного цикла (причина обязательна). */
export function useTransitionLifecycle(fieldId: string) {
  const qc = useQueryClient();
  return useMutation<
    { field_id: string; lifecycle_status: string; reason: string },
    Error,
    { status: 'active' | 'withdrawn' | 'archived'; reason: string }
  >({
    mutationFn: (body) => api.post(`/api/v1/fields/${fieldId}/lifecycle`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['field-card'] });
    },
  });
}
